import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { ArrowLeft, FileCheck, Lock, CheckCircle2, XCircle, Sparkles } from "lucide-react";

/**
 * Public 1040 practice return preview.
 * The first case (w2_employee) is free — no login required.
 * All others show a paywall.
 */
export default function Course1040Practice() {
  const { id } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setResult(null);
    setAnswers({});
    api.get(`/course1040/practice/${id}`)
      .then(r => setCaseData(r.data))
      .catch(e => {
        if (e?.response?.status === 402) setError({ code: 402, msg: e.response.data?.detail || "Locked" });
        else if (e?.response?.status === 404) setError({ code: 404, msg: "Practice case not found." });
        else setError({ code: "err", msg: "Something went wrong loading this practice case." });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/course1040/practice/submit", { case_id: id, answers });
      setResult(r.data);
      setTimeout(() => document.getElementById("practice-1040-result")?.scrollIntoView({ behavior: "smooth" }), 100);
    } finally { setSubmitting(false); }
  };

  const reset = () => { setAnswers({}); setResult(null); };

  if (loading) return <Layout><div className="max-w-3xl mx-auto px-6 py-14 text-muted-foreground">Loading practice return…</div></Layout>;

  if (error?.code === 402) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-14 text-center space-y-5" data-testid="practice-1040-paywall">
          <Lock size={48} className="mx-auto text-primary" />
          <div className="font-serif text-4xl">Practice locked</div>
          <p className="text-muted-foreground">
            {error.msg} The first practice return (Maria the W-2 employee) is free — enroll to unlock the other 3 cases (gig worker, rental owner, and more).
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild data-testid="practice-1040-paywall-buy"><Link to="/pricing?plan=student">Enroll — $175</Link></Button>
            <Button asChild variant="outline"><Link to="/course/1040">← Back to course</Link></Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) return <Layout><div className="max-w-3xl mx-auto px-6 py-14 text-muted-foreground">{error.msg}</div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid={`practice-1040-page-${caseData.id}`}>
        <div className="mb-2">
          <Link to="/course/1040" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="practice-1040-back">
            <ArrowLeft size={12} /> All 1040 chapters
          </Link>
        </div>
        <div className="mb-8">
          <div className="label-caps text-primary flex items-center gap-2">
            <FileCheck size={14} /> Practice Return
            {caseData.free_preview && <span className="text-[10px] font-semibold bg-primary/10 text-primary rounded px-2 py-0.5 ml-1">FREE PREVIEW</span>}
          </div>
          <h1 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">{caseData.title}</h1>
        </div>

        <div className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-2 space-y-4">
            <Card className="p-5" data-testid="practice-1040-scenario">
              <div className="label-caps mb-2 text-primary">Scenario</div>
              <p className="text-sm leading-relaxed">{caseData.scenario}</p>
            </Card>
            <Card className="p-5">
              <div className="label-caps mb-3 text-primary">Given data</div>
              <div className="space-y-2">
                {caseData.given_data.map((g, i) => (
                  <div key={i} className="flex justify-between gap-3 text-sm border-b border-border last:border-0 pb-1.5">
                    <span className="text-muted-foreground">{g.label}</span>
                    <span className="font-mono font-medium">{g.value}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div className="md:col-span-3">
            <Card className="p-6" data-testid="practice-1040-form">
              <div className="label-caps mb-3 text-primary flex items-center gap-2">
                <Sparkles size={12} /> Fill in the Form 1040 lines
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Enter each answer as it would appear on Form 1040 or in tax software. Round to whole dollars.
              </p>
              <form onSubmit={e => { e.preventDefault(); submit(); }} className="space-y-4">
                {caseData.fields.map(f => (
                  <div key={f.id}>
                    <Label className="label-caps text-[10px] mb-1 block">{f.label}</Label>
                    {f.hint && <div className="text-[11px] text-muted-foreground mb-1">Hint: {f.hint}</div>}
                    <Input
                      inputMode="decimal"
                      data-testid={`practice-1040-field-${f.id}`}
                      value={answers[f.id] ?? ""}
                      onChange={e => setAnswers({ ...answers, [f.id]: e.target.value })}
                      placeholder="$"
                      disabled={!!result}
                    />
                  </div>
                ))}
                <div className="pt-2 flex gap-2 flex-wrap">
                  {!result && (
                    <Button type="submit" data-testid="practice-1040-submit" disabled={submitting}>
                      {submitting ? "Grading…" : "Submit answers"}
                    </Button>
                  )}
                  {result && (
                    <Button type="button" variant="outline" onClick={reset} data-testid="practice-1040-retry">
                      Try again
                    </Button>
                  )}
                </div>
              </form>
            </Card>

            {result && (
              <Card
                id="practice-1040-result"
                data-testid="practice-1040-result"
                className={`p-6 mt-5 border ${result.percent === 100 ? "border-primary/40 bg-primary/5" : result.percent >= 60 ? "border-amber-400 bg-amber-50" : "border-red-400 bg-red-50"}`}
              >
                <div className="text-center mb-4">
                  <div className="label-caps text-primary">Your score</div>
                  <div className="font-serif text-4xl mt-1">{result.score} / {result.total}</div>
                  <div className="text-sm text-muted-foreground">{result.percent}%</div>
                </div>
                <div className="space-y-2">
                  {result.results.map(r => (
                    <div key={r.field_id} className="flex items-start gap-2 text-sm border-b border-border last:border-0 pb-2">
                      {r.is_correct ? <CheckCircle2 size={16} className="text-emerald-600 flex-none mt-0.5" /> : <XCircle size={16} className="text-red-600 flex-none mt-0.5" />}
                      <div className="flex-1">
                        <div className="font-medium">{r.label}</div>
                        <div className="text-xs text-muted-foreground">
                          Your answer: <span className="font-mono">{r.your_answer ?? "—"}</span>
                          {" · "}Correct: <span className="font-mono font-semibold">{r.correct_answer}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-border text-center">
                  <p className="text-sm text-muted-foreground mb-3">Enjoyed this? Enroll to unlock the other 3 practice returns (Devon the gig worker, the Chens' rental, and one final capstone).</p>
                  <Button asChild data-testid="practice-1040-enroll-after"><Link to="/pricing?plan=student">Enroll — $175</Link></Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
